import { PageHeader } from "../components/PageHeader";
import { Card, Lead, P, H3, Stat } from "../components/Section";
import { openLink, haptic } from "../lib/telegram";

type ProjectColor =
  | "blue" | "purple" | "green" | "orange" | "pink"
  | "teal" | "indigo" | "yellow" | "red" | "mint" | "brown";

interface Project {
  icon: string;
  color: ProjectColor;
  title: string;
  text: string;
  url?: string;
  /** Путь к превью-картинке (jpg/png, желательно 1200x675, соотношение 16:9). Если файл отсутствует — показывается градиентная заглушка. */
  image?: string;
  /** Градиент-фон для заглушки и подложки за картинкой. */
  gradient: string;
  /** Как вписывать картинку: cover — обрезать (для фото), contain — целиком на фоне (для скриншотов UI). */
  imageFit?: "cover" | "contain";
}

const projects: Project[] = [
  {
    icon: "🌿", color: "brown", title: "Cocoon Resort",
    text: "Life-experience wellness отель на севере Бали. 20 юнитов, breakeven 32%, средний ROI 19,5%.",
    url: "https://partner.residebali.com",
    image: "/projects/cocoon.png",
    gradient: "linear-gradient(135deg, #c4a380 0%, #6e5640 100%)",
  },
  {
    icon: "🏝", color: "teal", title: "RESIDE",
    text: "Экосистема недвижимости на Бали: бюро, инвест-клуб, управляющая компания.",
    url: "https://residebali.com",
    image: "/projects/reside.jpg",
    gradient: "linear-gradient(135deg, #5ac8fa 0%, #00838f 100%)",
  },
  {
    icon: "💰", color: "green", title: "FinKeeper",
    text: "Финансовый учёт для малого бизнеса. ДДС, П&Л, РНП + Telegram-бот.",
    url: "https://finkeeper.biz",
    image: "/projects/finkeeper.png",
    gradient: "linear-gradient(135deg, #34c759 0%, #1c8a3d 100%)",
    imageFit: "contain",
  },
  {
    icon: "🎤", color: "pink", title: "Бизнес-Пробуждение",
    text: "Крупнейший бизнес-форум Беларуси. 7000+ участников. №1 в стране.",
    image: "/projects/bp.jpg",
    gradient: "linear-gradient(135deg, #c8102e 0%, #6a0817 100%)",
  },
  {
    icon: "🚀", color: "indigo", title: "BOOST FORUM",
    text: "Крупнейший бизнес-форум в Европе.",
    image: "/projects/boost.jpg",
    gradient: "linear-gradient(135deg, #5856d6 0%, #2c2a8a 100%)",
  },
];

const iconBg: Record<ProjectColor, string> = {
  blue: "bg-[var(--icon-blue-bg)] text-[var(--icon-blue)]",
  purple: "bg-[var(--icon-purple-bg)] text-[var(--icon-purple)]",
  green: "bg-[var(--icon-green-bg)] text-[var(--icon-green)]",
  orange: "bg-[var(--icon-orange-bg)] text-[var(--icon-orange)]",
  pink: "bg-[var(--icon-pink-bg)] text-[var(--icon-pink)]",
  teal: "bg-[var(--icon-teal-bg)] text-[var(--icon-teal)]",
  indigo: "bg-[var(--icon-indigo-bg)] text-[var(--icon-indigo)]",
  yellow: "bg-[var(--icon-yellow-bg)] text-[var(--icon-yellow)]",
  red: "bg-[var(--icon-red-bg)] text-[var(--icon-red)]",
  mint: "bg-[var(--icon-mint-bg)] text-[var(--icon-mint)]",
  brown: "bg-[var(--icon-brown-bg)] text-[var(--icon-brown)]",
};

export default function About() {
  return (
    <div className="pb-10">
      <PageHeader
        eyebrow="Обо мне"
        title="Антон Короткий"
        subtitle="Предприниматель с 18-летним опытом. Основатель RESIDE и Cocoon Resort. Educational-инфлюенсер №1 в Беларуси."
      />

      <div className="px-5 mt-3 space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <Stat value="18" label="лет в бизнесе" />
          <Stat value="7K+" label="участников форумов" />
          <Stat value="90K" label="подписчиков IG" />
        </div>

        <Card>
          <Lead>
            Организатор самой крупной в истории Беларуси конференции «Бизнес-Пробуждение».
            Поставил рекорд страны: 7 000+ участников.
          </Lead>
          <P>
            Впервые привёз в Минск Ирину Хакамаду, Михаила Дашкиева, Ксению Собчак,
            Дмитрия Портнягина, Оскара Хартманна. Форум признан №1 в Беларуси.
          </P>
        </Card>

        <Card>
          <H3>Что обо мне ещё важно знать</H3>
          <ul className="space-y-2 mt-2 text-[15px] leading-snug text-secondary">
            <li>🏆 Организатор BOOST FORUM — крупнейшего бизнес-форума в Европе</li>
            <li>🏛 Соучредитель RESIDE, RESIDE INVEST CLUB и RESIDE TRUST на Бали</li>
            <li>📈 8 лет в маркетинге, основатель маркетингового агентства</li>
            <li>🥇 Победитель «Премии №1» — лучший education-инфлюенсер Беларуси</li>
            <li>🌴 Живу и работаю на Бали 2+ года</li>
          </ul>
        </Card>

        <div className="section-header">Мои проекты</div>
        <div className="flex flex-col gap-3">
          {projects.map((p) => (
            <button
              key={p.title}
              type="button"
              className={`ios-card overflow-hidden text-left w-full ${p.url ? "ios-card-press" : "cursor-default"}`}
              onClick={() => {
                if (p.url) {
                  haptic("light");
                  openLink(p.url);
                }
              }}
            >
              {/* Превью-картинка */}
              <div
                className="w-full relative"
                style={{ aspectRatio: "16 / 9", background: p.gradient }}
              >
                {p.image && (
                  <img
                    src={p.image}
                    alt={p.title}
                    className={`absolute inset-0 w-full h-full ${p.imageFit === "contain" ? "object-contain p-2" : "object-cover"}`}
                    onError={(e) => {
                      // Файл ещё не положен — оставляем градиентный фон без затемнения
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
                {/* Эмодзи как заглушка / акцент поверх картинки */}
                <div className="absolute inset-0 flex items-end justify-start p-3">
                  <div className="text-3xl drop-shadow-sm">{p.icon}</div>
                </div>
              </div>

              {/* Текст */}
              <div className="p-4">
                <div className="font-semibold text-ink text-[16px]">{p.title}</div>
                <div className="text-[14px] text-secondary mt-1 leading-snug">{p.text}</div>
                {p.url && (
                  <div className="text-[12px] text-link mt-2">
                    {p.url.replace(/^https?:\/\//, "")}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
